class SupabaseService {
  constructor() {
    this.urlKey = 'supabase_url';
    this.keyKey = 'supabase_anon_key';
    this.DEBUG = false; // 로그 제어 플래그
  }

  getUrl() { 
    // 환경변수에서 먼저 확인, 없으면 localStorage에서 확인
    return process.env.REACT_APP_SUPABASE_URL || localStorage.getItem(this.urlKey) || ''; 
  }
  getKey() { 
    // 환경변수에서 먼저 확인, 없으면 localStorage에서 확인
    return process.env.REACT_APP_SUPABASE_ANON_KEY || localStorage.getItem(this.keyKey) || ''; 
  }
  setConfig({ url, key }) {
    if (url) localStorage.setItem(this.urlKey, url);
    if (key) localStorage.setItem(this.keyKey, key);
  }
  isConfigured() {
    const url = this.getUrl();
    const key = this.getKey();
    
    if (this.DEBUG) console.log('Supabase 설정 확인:', {
      url: url,
      key: key ? '설정됨' : '설정되지 않음',
      envUrl: process.env.REACT_APP_SUPABASE_URL,
      envKey: process.env.REACT_APP_SUPABASE_ANON_KEY ? '설정됨' : '설정되지 않음'
    });
    
    // URL이 이메일 형식이거나 잘못된 형식인지 확인
    if (url && (url.includes('@gmail.com') || url.includes('@') || !url.includes('supabase.co'))) {
      console.error('잘못된 Supabase URL:', url);
      return false;
    }
    
    return !!url && !!key;
  }

  // 설정 초기화 메서드 추가
  clearConfig() {
    localStorage.removeItem(this.urlKey);
    localStorage.removeItem(this.keyKey);
  }

  async request(path, { method = 'GET', body, api = 'rest' } = {}) {
    // 로컬 개발 환경에서는 직접 Supabase API 호출, 프로덕션에서는 프록시 사용
    const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    if (isLocalDev) {
      // 로컬 개발 환경: 직접 Supabase API 호출
      const baseUrl = api === 'auth' ? `${this.getUrl()}/auth/v1` : `${this.getUrl()}/rest/v1`;
      const url = `${baseUrl}/${path}`;
      
      const res = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'apikey': this.getKey(),
          'Authorization': `Bearer ${this.getKey()}`,
          'Prefer': 'return=representation'
        },
        body: body ? JSON.stringify(body) : undefined
      });
      
      if (!res.ok) {
        const err = await res.text();
        throw new Error(`Supabase 오류: ${res.status} ${err}`);
      }
      
      // 응답이 비어있을 수 있으므로 처리
      const text = await res.text();
      if (!text) {
        return { success: true }; // 빈 응답은 성공으로 간주
      }
      
      try {
        return JSON.parse(text);
      } catch (e) {
        if (this.DEBUG) console.warn('JSON 파싱 실패, 빈 객체 반환:', text);
        return { success: true };
      }
    } else {
      // 프로덕션 환경: 프록시를 통해 호출
      const url = `/api/supabase-proxy?path=${encodeURIComponent(path)}&method=${encodeURIComponent(method)}&api=${encodeURIComponent(api)}`;
      const res = await fetch(url, {
        method: ['POST','PATCH','PUT'].includes(method) ? 'POST' : 'GET',
        headers: { 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined
      });
      if (!res.ok) {
        const err = await res.text();
        throw new Error(`Supabase 오류: ${res.status} ${err}`);
      }
      return await res.json().catch(() => ({}));
    }
  }

  async upsertUser(user) {
    if (this.DEBUG) console.log('upsertUser 호출:', user);
    
    try {
      // 먼저 기존 사용자가 있는지 확인
      const existingUser = await this.getUserData(user.email);
      
      if (existingUser) {
        // 기존 사용자가 있으면 업데이트
        if (this.DEBUG) console.log('기존 사용자 업데이트:', existingUser);
        return await this.request(`users?email=eq.${encodeURIComponent(user.email)}`, {
          method: 'PATCH',
          body: {
            name: user.name || null,
            phone: user.phone || null,
            password: user.password || null,
            points: user.points ?? 0
          }
        });
      } else {
        // 새 사용자 생성
        if (this.DEBUG) console.log('새 사용자 생성');
        return await this.request('users', {
          method: 'POST',
          body: {
            email: user.email,
            name: user.name || null,
            phone: user.phone || null,
            password: user.password || null,
            points: user.points ?? 1500 // 신규 사용자 기본 포인트
          }
        });
      }
    } catch (error) {
      console.error('upsertUser 오류:', error);
      throw error;
    }
  }

  async listUsers(limit = 100) {
    return this.request(`users?select=*&order=created_at.desc&limit=${limit}`);
  }

  async updateUserPoints(userId, points) {
    if (this.DEBUG) console.log('updateUserPoints 호출:', { userId, points });
    
    const result = await this.request(`users?id=eq.${userId}`, {
      method: 'PATCH',
      body: { points: parseInt(points) }
    });
    
    if (this.DEBUG) console.log('updateUserPoints 결과:', result);
    return result;
  }

  // 이메일 기반 포인트 업데이트 (백업용)
  async updateUserPointsByEmail(email, points) {
    if (this.DEBUG) console.log('updateUserPointsByEmail 호출:', { email, points });
    
    const result = await this.request(`users?email=eq.${encodeURIComponent(email)}`, {
      method: 'PATCH',
      body: { points: parseInt(points) }
    });
    
    if (this.DEBUG) console.log('updateUserPointsByEmail 결과:', result);
    return result;
  }

  // Supabase Auth 메서드들
  async signUp(email, password, metadata = {}) {
    if (!this.isConfigured()) {
      if (this.DEBUG) console.error('Supabase 설정 확인:', {
        url: this.getUrl(),
        key: this.getKey() ? '설정됨' : '설정되지 않음'
      });
      throw new Error('Supabase가 설정되지 않았습니다. 관리자에게 문의하세요.');
    }

    if (this.DEBUG) console.log('Supabase 회원가입 시도:', { email, metadata, url: this.getUrl() });

    return await this.request('signup', {
      method: 'POST',
      api: 'auth',
      body: {
        email,
        password,
        options: {
          email_redirect_to: `${window.location.origin}/auth/callback`,
          data: metadata // 추가 정보를 metadata에 포함
        }
      }
    });
  }

  async signIn(email, password) {
    if (!this.isConfigured()) {
      throw new Error('Supabase가 설정되지 않았습니다.');
    }

    const data = await this.request('token?grant_type=password', {
      method: 'POST',
      api: 'auth',
      body: {
        email,
        password
      }
    });

    // 토큰을 localStorage에 저장
    if (data.access_token) {
      localStorage.setItem('supabase_auth_token', data.access_token);
      localStorage.setItem('supabase_user', JSON.stringify(data.user));
    }

    return data;
  }

  async signOut() {
    const token = localStorage.getItem('supabase_auth_token');
    if (token && this.isConfigured()) {
      try {
        await fetch(`${this.getUrl()}/auth/v1/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': this.getKey(),
            'Authorization': `Bearer ${token}`
          }
        });
      } catch (error) {
        if (this.DEBUG) console.error('로그아웃 오류:', error);
      }
    }
    
    // 로컬 저장소에서 토큰 제거
    localStorage.removeItem('supabase_auth_token');
    localStorage.removeItem('supabase_user');
  }

  getCurrentUser() {
    const userStr = localStorage.getItem('supabase_user');
    return userStr ? JSON.parse(userStr) : null;
  }

  isAuthenticated() {
    const token = localStorage.getItem('supabase_auth_token');
    const user = this.getCurrentUser();
    return !!(token && user);
  }

  async getUserData(email) {
    if (!this.isConfigured()) {
      throw new Error('Supabase가 설정되지 않았습니다.');
    }

    try {
      const response = await fetch(`${this.getUrl()}/rest/v1/users?email=eq.${encodeURIComponent(email)}&select=*`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'apikey': this.getKey(),
          'Authorization': `Bearer ${this.getKey()}`
        }
      });

      if (!response.ok) {
        throw new Error('사용자 정보를 가져올 수 없습니다.');
      }

      const data = await response.json();
      return data && data.length > 0 ? data[0] : null;
    } catch (error) {
      if (this.DEBUG) console.error('사용자 데이터 조회 오류:', error);
      return null;
    }
  }


  async updateUserProfile(email, partialProfile) {
    if (!this.isConfigured()) {
      throw new Error('Supabase가 설정되지 않았습니다.');
    }
    try {
      const response = await fetch(`${this.getUrl()}/rest/v1/users?email=eq.${encodeURIComponent(email)}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'apikey': this.getKey(),
          'Authorization': `Bearer ${this.getKey()}`
        },
        body: JSON.stringify(partialProfile)
      });
      if (!response.ok) {
        throw new Error('프로필 업데이트에 실패했습니다.');
      }
      return await response.json();
    } catch (error) {
      if (this.DEBUG) console.error('프로필 업데이트 오류:', error);
      throw error;
    }
  }
}

const supabaseService = new SupabaseService();
export default supabaseService;


